import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import fetchAPI from '../fetchAPI';

/**
 * How an agency is sold: seats, or consumption.
 *
 * 'license' — a seat count and an expiry on the company's licence rows. The
 *             licence cron and both login paths enforce them.
 * 'token'   — SRT consumption only. No seat cap, no expiry, and no licence row
 *             is required or read.
 *
 * Nothing here is a billing switch: the invoice is consumption-based either
 * way. What this changes is whether the seat gate runs at all.
 */
export type BillingModel = 'license' | 'token';

const COMPANY_KEY = 'company';

export const useCompanyBillingModel = (companyId?: string) =>
  useQuery({
    queryKey: [COMPANY_KEY, companyId, 'billing-model'],
    enabled: Boolean(companyId),
    queryFn: async () => {
      // handleGET puts the whole response body in `data`, and the server nests
      // its payload one level further ({ message, data }). Reading one level too
      // shallow always missed the field, so every account showed 'license' no
      // matter what was saved.
      const response: iResponse<{ data?: { billingModel?: BillingModel } }> = await fetchAPI({
        method: 'GET',
        route: `company/${companyId}`
      });
      if (response.code >= 400) {
        throw new Error(response.error?.message ?? 'Could not load the billing model.');
      }
      // Older companies predate the column; the server defaults it, and so do
      // we, so the screen never renders an empty selector.
      return (response.data?.data?.billingModel ?? 'license') as BillingModel;
    }
  });

export const useSetCompanyBillingModel = (companyId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (billingModel: BillingModel) => {
      const response: iResponse<unknown> = await fetchAPI({
        method: 'PATCH',
        route: `company/${companyId}`,
        data: { billingModel } as APIData
      });
      // fetchAPI hands 4xx back as data rather than throwing, so without this a
      // rejected change would land in onSuccess and the selector would show the
      // new value while the server kept the old one.
      if (response.code >= 400) {
        throw new Error(response.error?.message ?? 'Could not change the billing model.');
      }
      return billingModel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANY_KEY, companyId] });
    }
  });
};
